import { DataGrid } from "devextreme-react";
import {
  Button,
  Column,
  Editing,
  FilterRow,
  Item,
  RangeRule,
  RequiredRule,
  Scrolling,
  Sorting,
  Toolbar,
} from "devextreme-react/data-grid";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toastMsg } from "../utils";
import { alert } from "devextreme/ui/dialog";

const DataGridWrapper = styled.div`
  #holidayGrid {
    max-height: calc(100vh - 260px);
  }
  .dx-datebox-calendar .dx-dropdowneditor-icon::before {
    content: "\f026";
  }
  .dx-toolbar {
    margin-bottom: 4px;
  }
  .dx-toolbar .dx-toolbar-label {
    font-weight: bold;
  }
  .dx-button {
    margin: 0 4px;
  }
  .dx-cell-modified:not(.dx-datagrid-invalid) {
    background-color: transparent !important;
  }
`;

const ConfigureHoliday = ({ item }) => {
  const currentYear = new Date().getFullYear(); // 현재 연도
  const [isEditMode, setEditMode] = useState(null);
  const [holidayDataSource, setHolidayDataSource] = useState(null);
  const [year, setYear] = useState(currentYear);

  const holidayGridRef = useRef(null);

  useEffect(() => {
    if (holidayGridRef.current && holidayDataSource) {
      if (isEditMode) {
        holidayGridRef.current.instance.option(
          "dataSource",
          holidayDataSource.map((el) => ({ ...el }))
        ); // cell Editing이라 holidayDataSource를 deep copy한 array를 dataSource로 설정.
      } else {
        holidayGridRef.current.instance.option("dataSource", holidayDataSource);
      }
    }
  }, [isEditMode]);

  useEffect(() => {
    console.log("mount");
    setTimeout(() => {
      const data = [
        { holiday: "신정", date: "2023-01-01" },
        { holiday: "설날", date: "2023-01-21" },
        { holiday: "설날", date: "2023-01-23" },
        { holiday: "설날(대체)", date: "2023-01-24" },
      ];
      setHolidayDataSource(data);
      if (holidayGridRef.current) {
        holidayGridRef.current.instance.option("dataSource", data);
      }
    }, 500);
  }, [year]);

  const handleChangeYear = (e) => {
    setYear(e.value);
  };

  const handleClickSave = () => {
    if (validateGridValue(holidayGridRef)) {
      toastMsg("저장", true);
      toggleEdit();
    }
  };

  const handleDeleteRow = () => {};

  const handleInitNewRow = (e) => {
    e.data.holiday = null;
    e.data.date = null;
  };

  const toggleEdit = () => {
    setEditMode(!isEditMode);
  };

  const validateGridValue = (ref) => {
    const gridInstance = ref.current.instance;
    const validationState = gridInstance.getController("validating")._validationState;
    const errorMessages = [];

    const visibleRowKeys = gridInstance.getVisibleRows().map((row) => row.key);

    validationState.forEach((row) => {
      if (visibleRowKeys.includes(row.key) && row.validationResults) {
        const cellValidation = Object.values(row.validationResults);
        cellValidation.forEach((c) => {
          if (c.status === "invalid") {
            const rule = c.brokenRule;
            const message = rule.type === "required" ? `${c.name}은 필수 항목입니다` : rule.message;
            errorMessages.push(message);
          }
        });
      }
    });

    if (errorMessages.length > 0) {
      alert(errorMessages.join("<br />"), "Validation Error");
      return false;
    }
    return true;
  };

  return (
    <DataGridWrapper>
      <DataGrid
        // dataSource={holidayDataSource}
        id="holidayGrid"
        ref={holidayGridRef}
        showBorders={true}
        showColumnLines={true}
        width={"100%"}
        wordWrapEnabled={true}
        onInitNewRow={handleInitNewRow}
      >
        <Editing
          allowAdding={isEditMode}
          allowDeleting={isEditMode}
          allowUpdating={isEditMode}
          mode="cell"
        />
        <FilterRow visible={true} />
        <Scrolling mode="virtual" showScrollbar={"always"} />
        <Sorting mode="multiple" />
        <Toolbar>
          <Item
            name="yearSelectBox"
            location="before"
            options={{
              dataSource: [
                currentYear - 1,
                currentYear,
                currentYear + 1,
                currentYear + 2,
              ],
              displayExpr: (e) => `${e}년`,
              onValueChanged: handleChangeYear,
              value: year,
            }}
            visible={!isEditMode}
            widget="dxSelectBox"
          />
          <Item
            name="yearTitle"
            location="before"
            text={`${year}년 회사 휴일`}
            visible={isEditMode}
          />
          <Item
            name="editButton"
            location="after"
            options={{
              icon: "edit",
              onClick: toggleEdit,
              stylingMode: "contained",
              type: "normal",
            }}
            visible={!isEditMode}
            widget="dxButton"
          />
          <Item
            name="addRowButton"
            location="after"
            // options={{ icon: "add", onClick: handleAddRow, stylingMode: "contained", type: "normal" }}
            // visible={isEditMode}
            // widget="dxButton"
          />
          <Item
            name="saveBtn"
            location="after"
            options={{
              icon: "save",
              onClick: handleClickSave,
              stylingMode: "contained",
              type: "normal",
            }}
            visible={isEditMode}
            widget="dxButton"
          />
          <Item
            name="revertBtn"
            location="after"
            options={{
              icon: "revert",
              onClick: toggleEdit,
              stylingMode: "contained",
              type: "normal",
            }}
            visible={isEditMode}
            widget="dxButton"
          />
        </Toolbar>
        <Column
          dataField="holiday"
          alignment="center"
          caption="공휴일"
          dataType="string"
        >
          <RequiredRule message="" />
        </Column>
        <Column
          dataField="date"
          alignment="center"
          caption="날짜"
          dataType="date"
          editorOptions={{ useMaskBehavior: true }}
          format="yyyy-MM-dd"
          showEditorAlways={isEditMode}
          sortIndex={0}
          sortOrder="asc"
        >
          <RequiredRule message="" />
          <RangeRule
            ignoreEmptyValue={true}
            max={new Date(`${year}-12-31T00:00:00`)}
            message={`${year}-01-01 ~ ${year}-12-31 사이의 날짜여야 합니다`}
            min={new Date(`${year}-01-01T00:00:00`)}
            reevaluate={true}
          />
        </Column>
        <Column type="buttons" visible={isEditMode} width={50}>
          <Button icon="trash" onClick={handleDeleteRow} />
        </Column>
      </DataGrid>
    </DataGridWrapper>
  );
};

export default ConfigureHoliday;
